export function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 45) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const days = Math.floor(h / 24);
  if (days < 7) return `${days}d`;
  const weeks = Math.floor(days / 7);
  if (weeks < 5) return `${weeks}w`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export function fullTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

/** Per-message stamp: date + time, rendered in the viewer's chosen profile timezone. */
export function messageStamp(iso, tz) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const opts = { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" };
  try {
    return d.toLocaleString(undefined, tz ? { ...opts, timeZone: tz } : opts);
  } catch (e) {
    return d.toLocaleString(undefined, opts);
  }
}

export function eventDateParts(iso, tz) {
  if (!iso) return {};
  const d = new Date(iso);
  if (isNaN(d.getTime())) return {};
  const fmt = (opts) => {
    try {
      return d.toLocaleString(undefined, tz ? { ...opts, timeZone: tz } : opts);
    } catch (e) {
      return d.toLocaleString(undefined, opts);
    }
  };
  return {
    month: fmt({ month: "short" }),
    day: fmt({ day: "numeric" }),
    weekday: fmt({ weekday: "short" }),
    year: fmt({ year: "numeric" }),
    time: fmt({ hour: "numeric", minute: "2-digit" }),
    // Abbreviated weekday keeps the event header compact: "Wed, November 5, 2026".
    dateLong: fmt({ weekday: "short", month: "long", day: "numeric", year: "numeric" }),
  };
}

/** The browser's IANA zone, e.g. "America/Chicago". */
export function browserTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  } catch (e) {
    return "";
  }
}

function zoneOffsetMs(date, tz) {
  const p = new Intl.DateTimeFormat("en-US", {
    timeZone: tz, hour12: false, year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  }).formatToParts(date).reduce((a, x) => (x.type === "literal" ? a : { ...a, [x.type]: x.value }), {});
  const asUtc = Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour % 24, +p.minute, +p.second);
  return asUtc - date.getTime();
}

/**
 * Event times are stored as a naive wall clock ("2026-08-14T12:00") that belongs to the
 * event's own zone. Resolve that to a real instant so it can be shown in any other zone.
 */
export function wallClockInstant(iso, tz) {
  if (!iso) return null;
  const naive = !/(Z|[+-]\d{2}:?\d{2})$/.test(iso);
  if (!naive || !tz) {
    const d = new Date(iso);
    return isNaN(d.getTime()) ? null : d;
  }
  const guess = new Date(`${iso.length === 16 ? `${iso}:00` : iso}Z`);
  if (isNaN(guess.getTime())) return null;
  try {
    const off = zoneOffsetMs(guess, tz);
    const d = new Date(guess.getTime() - off);
    const off2 = zoneOffsetMs(d, tz);
    return off2 === off ? d : new Date(guess.getTime() - off2);
  } catch (e) {
    const d = new Date(iso);
    return isNaN(d.getTime()) ? null : d;
  }
}

/** Short zone name for a moment in a zone, e.g. "CDT". Falls back to the IANA name. */
export function zoneAbbr(iso, tz) {
  const d = wallClockInstant(iso, tz);
  if (!tz || !d) return "";
  try {
    const part = new Intl.DateTimeFormat("en-US", { timeZone: tz, timeZoneName: "short" })
      .formatToParts(d).find((x) => x.type === "timeZoneName");
    return part ? part.value : tz;
  } catch (e) {
    return tz;
  }
}

/** "MDT" when English has a letter abbreviation for that zone, else "" so callers keep the
 *  city name — most of the world only yields "GMT+5:30", which reads worse than "Asia/Kolkata". */
export function zoneLetterAbbr(iso, tz) {
  const abbr = zoneAbbr(iso, tz);
  if (!abbr || abbr.includes("/") || /^(GMT|UTC)/.test(abbr)) return "";
  return abbr;
}

/** "4:00 PM your time" — only when the viewer's zone shows a different clock time. */
export function viewerTimeHint(iso, eventTz, viewerTz) {
  const mine = viewerTz || browserTimezone();
  if (!iso || !eventTz || !mine || mine === eventTz) return "";
  const d = wallClockInstant(iso, eventTz);
  if (!d) return "";
  const at = (tz) => {
    try {
      return d.toLocaleString(undefined, { timeZone: tz, hour: "numeric", minute: "2-digit", month: "short", day: "numeric" });
    } catch (e) {
      return "";
    }
  };
  const theirs = at(eventTz);
  const ours = at(mine);
  return !ours || ours === theirs ? "" : ours;
}
